import React, { useState, useEffect, useCallback } from 'react';
import { GUITAR_STRINGS, STRING_THEMES, getNoteAtFret } from '../../utils/guitarTheory';
import { soundEngine } from '../../utils/audio';
import { Trophy, Flame, RotateCcw, Sparkles, Volume2, Music, CheckCircle2 } from 'lucide-react';
import confetti from 'canvas-confetti';

interface TargetPrompt {
  stringIndex: number; // 0 (High E) to 5 (Low E)
  fret: number;
  note: string;
}

interface InspectedNoteInfo {
  note: string;
  octave: number;
  noteWithOctave: string;
  freq: number;
  stringIdx: number;
  fret: number;
}

export const FretboardGame: React.FC = () => {
  // Free Fretboard Explorer comes first by default
  const [gameMode, setGameMode] = useState<'explorer' | 'quest'>('explorer');
  const [showAllNotes, setShowAllNotes] = useState(true);
  const [showOctaves, setShowOctaves] = useState(true);
  const [colorByString, setColorByString] = useState(true);
  const [target, setTarget] = useState<TargetPrompt | null>(null);
  const [score, setScore] = useState(0);
  const [streak, setStreak] = useState(0);
  const [bestStreak, setBestStreak] = useState(0);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [activeFret, setActiveFret] = useState<{ stringIdx: number; fret: number } | null>(null);
  const [inspectedNote, setInspectedNote] = useState<InspectedNoteInfo | null>(null);

  // Generate a new random target prompt for Quest
  const generateNewTarget = useCallback(() => {
    const randomStringIdx = Math.floor(Math.random() * 6);
    const randomFret = Math.floor(Math.random() * 13);
    const { note } = getNoteAtFret(randomStringIdx, randomFret);

    setTarget({
      stringIndex: randomStringIdx,
      fret: randomFret,
      note,
    });
    setFeedback(null);
  }, []);

  useEffect(() => {
    if (gameMode === 'quest' && !target) {
      generateNewTarget();
    }
  }, [gameMode, target, generateNewTarget]);

  // Click handler for any fret
  const handleFretClick = (stringIdx: number, fret: number) => {
    const noteData = getNoteAtFret(stringIdx, fret);
    soundEngine.playGuitarPluck(noteData.freq, 2.2, 0.55);
    setActiveFret({ stringIdx, fret });
    setInspectedNote({ ...noteData, stringIdx, fret });

    setTimeout(() => setActiveFret(null), 600);

    if (gameMode === 'explorer') {
      return;
    }

    if (!target) return;

    const isCorrect = stringIdx === target.stringIndex && fret === target.fret;

    if (isCorrect) {
      soundEngine.playSuccessSfx();
      const newStreak = streak + 1;
      setStreak(newStreak);
      setScore(score + 10 + Math.min(newStreak * 2, 20));
      if (newStreak > bestStreak) {
        setBestStreak(newStreak);
      }

      setFeedback({
        type: 'success',
        message: `Yay! Note ${noteData.note} found on ${GUITAR_STRINGS[stringIdx].name}! 🌸`,
      });

      confetti({
        particleCount: 35,
        spread: 70,
        origin: { y: 0.6 },
        colors: ['#F472B6', '#C084FC', '#38BDF8', '#FDE047'],
      });

      setTimeout(() => {
        generateNewTarget();
      }, 1400);
    } else {
      soundEngine.playIncorrectSfx();
      setStreak(0);
      setFeedback({
        type: 'error',
        message: `Oops! You played ${noteData.note} on ${GUITAR_STRINGS[stringIdx].name}. Look for ${target.note} on ${GUITAR_STRINGS[target.stringIndex].name}!`,
      });
    }
  };

  const handleResetQuest = () => {
    soundEngine.playPopSfx();
    setScore(0);
    setStreak(0);
    generateNewTarget();
  };

  const isMarkerFret = (fret: number) => [3, 5, 7, 9, 12].includes(fret);

  return (
    <div className="space-y-6">
      
      {/* Menu Bar: Free Fretboard Explorer comes FIRST */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white p-4 sm:p-6 rounded-3xl border-2 border-pink-200 shadow-sm">
        
        {/* Mode Buttons */}
        <div className="flex items-center gap-2">
          {/* 1. Free Fretboard Explorer (First) */}
          <button
            onClick={() => {
              soundEngine.playPopSfx();
              setGameMode('explorer');
            }}
            className={`px-4 py-2.5 rounded-2xl font-bold text-xs sm:text-sm transition-all cursor-pointer flex items-center gap-2 ${
              gameMode === 'explorer'
                ? 'bg-gradient-to-r from-purple-500 via-pink-500 to-rose-500 text-white shadow-md shadow-pink-200 scale-102'
                : 'bg-purple-50 text-purple-700 hover:bg-purple-100'
            }`}
          >
            <span>🔍 Free Fretboard Explorer</span>
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/20 text-white">Interactive</span>
          </button>

          {/* 2. Note Hunter Quest (Second) */}
          <button
            onClick={() => {
              soundEngine.playPopSfx();
              setGameMode('quest');
            }}
            className={`px-4 py-2.5 rounded-2xl font-bold text-xs sm:text-sm transition-all cursor-pointer flex items-center gap-2 ${
              gameMode === 'quest'
                ? 'bg-gradient-to-r from-pink-500 to-rose-500 text-white shadow-md shadow-pink-200 scale-102'
                : 'bg-pink-50 text-pink-700 hover:bg-pink-100'
            }`}
          >
            <span>🎯 Note Hunter Quest</span>
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/20 text-white">Game</span>
          </button>
        </div>

        {/* Explorer Controls vs Quest Score */}
        {gameMode === 'explorer' ? (
          <div className="flex flex-wrap items-center gap-3">
            <label className="text-xs font-bold text-gray-700 flex items-center gap-1.5 cursor-pointer select-none bg-pink-50 px-3 py-1.5 rounded-xl border border-pink-100 hover:bg-pink-100">
              <input
                type="checkbox"
                checked={colorByString}
                onChange={(e) => {
                  soundEngine.playPopSfx();
                  setColorByString(e.target.checked);
                }}
                className="w-4 h-4 accent-pink-500 rounded cursor-pointer"
              />
              <span>🎨 String Colors</span>
            </label>

            <label className="text-xs font-bold text-gray-700 flex items-center gap-1.5 cursor-pointer select-none bg-purple-50 px-3 py-1.5 rounded-xl border border-purple-100 hover:bg-purple-100">
              <input
                type="checkbox"
                checked={showOctaves}
                onChange={(e) => {
                  soundEngine.playPopSfx();
                  setShowOctaves(e.target.checked);
                }}
                className="w-4 h-4 accent-purple-500 rounded cursor-pointer"
              />
              <span>🔢 Octaves (E₄, B₃)</span>
            </label>

            <label className="text-xs font-bold text-gray-700 flex items-center gap-1.5 cursor-pointer select-none bg-rose-50 px-3 py-1.5 rounded-xl border border-rose-100 hover:bg-rose-100">
              <input
                type="checkbox"
                checked={showAllNotes}
                onChange={(e) => {
                  soundEngine.playPopSfx();
                  setShowAllNotes(e.target.checked);
                }}
                className="w-4 h-4 accent-rose-500 rounded cursor-pointer"
              />
              <span>Show All Notes</span>
            </label>
          </div>
        ) : (
          <div className="flex items-center gap-3 self-end sm:self-auto">
            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-amber-50 border border-amber-200 text-amber-900 text-xs font-bold">
              <Trophy className="w-4 h-4 text-amber-500" />
              <span>Score: {score}</span>
            </div>

            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-orange-50 border border-orange-200 text-orange-600 text-xs font-bold">
              <Flame className="w-4 h-4 fill-current" />
              <span>Streak: {streak}</span>
            </div>

            <button
              onClick={handleResetQuest}
              className="p-1.5 rounded-xl bg-gray-100 text-gray-500 hover:bg-gray-200 cursor-pointer"
              title="Reset Quest"
            >
              <RotateCcw className="w-4 h-4" />
            </button>
          </div>
        )}

      </div>

      {/* String Color Legend (shows the unique color identity of each of the 6 strings) */}
      {gameMode === 'explorer' && (
        <div className="p-4 rounded-2xl bg-white border border-pink-100 shadow-xs flex flex-wrap items-center justify-between gap-3 text-xs">
          <div className="font-bold text-purple-900 flex items-center gap-1.5">
            <Sparkles className="w-4 h-4 text-pink-500" />
            <span>String Color Palette:</span>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {STRING_THEMES.map((theme) => (
              <span
                key={theme.stringIdx}
                className={`px-2.5 py-1 rounded-full font-bold text-[11px] border shadow-2xs flex items-center gap-1.5 ${theme.chipBg} ${theme.chipText} border-current/20`}
              >
                <span className={`w-2.5 h-2.5 rounded-full ${theme.badgeBg}`} />
                <span>{theme.name}: {theme.colorName}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Inspected Note Information Bar */}
      {inspectedNote && (
        <div className="p-4 rounded-2xl bg-gradient-to-r from-pink-100/90 via-purple-100/80 to-rose-100/90 border-2 border-pink-300 shadow-xs flex flex-col sm:flex-row items-center justify-between gap-3 animate-fadeIn">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-2xl flex items-center justify-center font-extrabold text-xl shadow-md border-2 border-white ${
              STRING_THEMES[inspectedNote.stringIdx].badgeBg
            } ${STRING_THEMES[inspectedNote.stringIdx].textColor}`}>
              {inspectedNote.note}
              {showOctaves && (
                <sub className="text-xs font-bold ml-0.5">{inspectedNote.octave}</sub>
              )}
            </div>
            <div>
              <div className="text-xs font-bold text-gray-500 uppercase">
                {GUITAR_STRINGS[inspectedNote.stringIdx].name} • Fret {inspectedNote.fret === 0 ? 'Open (0)' : inspectedNote.fret}
              </div>
              <div className="text-base font-extrabold text-purple-950 flex items-center gap-2">
                <span>Note {inspectedNote.note} ({STRING_THEMES[inspectedNote.stringIdx].colorName})</span>
                <span className="text-xs font-normal text-purple-700">~{inspectedNote.freq.toFixed(1)} Hz</span>
              </div>
            </div>
          </div>

          <button
            onClick={() => handleFretClick(inspectedNote.stringIdx, inspectedNote.fret)}
            className="px-4 py-2 rounded-xl bg-white text-purple-700 font-bold text-xs shadow-xs hover:bg-purple-50 flex items-center gap-1.5 cursor-pointer transition-transform active:scale-95"
          >
            <Volume2 className="w-4 h-4 text-pink-500" />
            <span>Play Again 🎶</span>
          </button>
        </div>
      )}

      {/* Quest Target Challenge Banner (when in Quest mode) */}
      {gameMode === 'quest' && target && (
        <div className="p-5 rounded-3xl bg-gradient-to-r from-pink-100 via-purple-100 to-rose-100 border-2 border-pink-300 text-center space-y-2 shadow-xs">
          <div className="text-xs font-bold uppercase tracking-wide text-pink-700 flex items-center justify-center gap-1">
            <Sparkles className="w-3.5 h-3.5" />
            <span>Melodalis Note Challenge</span>
          </div>

          <div className="text-2xl sm:text-3xl font-extrabold text-purple-900">
            Find Note <span className="text-pink-600 underline decoration-wavy decoration-pink-300">{target.note}</span> on the <span className="text-indigo-600">{GUITAR_STRINGS[target.stringIndex].name}</span> string!
          </div>

          <p className="text-xs text-purple-800/80">
            Click on the correct fret along the guitar neck below. Fret 0 is the open string!
          </p>

          {feedback && (
            <div className={`mt-2 py-2 px-4 rounded-xl text-xs font-bold inline-block animate-bounce ${
              feedback.type === 'success' ? 'bg-green-100 text-green-700 border border-green-300' : 'bg-rose-100 text-rose-700 border border-rose-300'
            }`}>
              {feedback.message}
            </div>
          )}
        </div>
      )}

      {/* Interactive Fretboard Visualizer (Horizontal Scrollable on Mobile) */}
      <div className="bg-amber-950 p-4 sm:p-6 rounded-3xl shadow-xl border-4 border-amber-900 overflow-x-auto">
        
        {/* Fretboard Inner Neck */}
        <div className="min-w-[800px] select-none">
          
          {/* Top Fret Number Headers (0 to 12) */}
          <div className="grid grid-cols-[90px_repeat(13,1fr)] text-center text-amber-200 text-xs font-bold pb-2 border-b border-amber-800">
            <div>String</div>
            {Array.from({ length: 13 }).map((_, f) => (
              <div key={f} className="flex flex-col items-center">
                <span>{f === 0 ? 'Open (0)' : f}</span>
                {isMarkerFret(f) && (
                  <span className="text-[10px] text-amber-400">
                    {f === 12 ? '●●' : '●'}
                  </span>
                )}
              </div>
            ))}
          </div>

          {/* 6 Guitar Strings (from 0: High E down to 5: Low E) */}
          <div className="divide-y divide-amber-900/60 my-2">
            {[0, 1, 2, 3, 4, 5].map((strIdx) => {
              const stringInfo = GUITAR_STRINGS[strIdx];
              const theme = STRING_THEMES[strIdx];

              return (
                <div key={strIdx} className="grid grid-cols-[90px_repeat(13,1fr)] items-center h-13 relative group">
                  
                  {/* Left Label: String Name with String Color Indicator */}
                  <div className="pr-2 font-bold text-xs text-amber-100 flex items-center justify-between">
                    <div className="flex items-center gap-1.5">
                      <span className={`w-2.5 h-2.5 rounded-full ${theme.badgeBg}`} />
                      <span>{stringInfo.name.split(' ')[0]}</span>
                    </div>
                    <span className="text-[11px] font-black" style={{ color: colorByString ? undefined : '#fde047' }}>
                      {stringInfo.baseNote}
                    </span>
                  </div>

                  {/* Horizontal Metallic Guitar String Line running through all frets */}
                  <div
                    className="absolute left-[90px] right-0 pointer-events-none z-10 shadow-sm"
                    style={{
                      height: `${Math.max(1.8, 4.2 - strIdx * 0.45)}px`,
                      top: 'calc(50% - 1px)',
                      background: colorByString
                        ? `linear-gradient(to right, rgba(255,255,255,0.7), rgba(200,200,200,0.9))`
                        : undefined,
                    }}
                  />

                  {/* 13 Fret Slots (0 to 12) */}
                  {Array.from({ length: 13 }).map((_, fret) => {
                    const { note, freq, isNatural, octave, noteWithOctave } = getNoteAtFret(strIdx, fret);
                    const isTargetFret = target && target.stringIndex === strIdx && target.fret === fret;
                    const isActive = activeFret && activeFret.stringIdx === strIdx && activeFret.fret === fret;
                    const isInspected = inspectedNote && inspectedNote.stringIdx === strIdx && inspectedNote.fret === fret;

                    // String-specific color styling
                    const stringBadgeStyle = colorByString
                      ? isNatural
                        ? `${theme.badgeBg} ${theme.textColor} border ${theme.badgeBorder}`
                        : `bg-slate-900 text-white border-2 ${theme.badgeBorder}`
                      : isNatural
                      ? 'bg-white text-gray-800 border border-gray-300'
                      : 'bg-pink-400 text-white';

                    return (
                      <button
                        key={fret}
                        onClick={() => handleFretClick(strIdx, fret)}
                        className={`h-full relative border-r-2 ${
                          fret === 0 ? 'border-amber-400 border-r-4' : 'border-amber-600/50'
                        } flex items-center justify-center hover:bg-amber-800/40 transition-colors cursor-pointer z-20`}
                        title={`${stringInfo.name} - Fret ${fret}: Note ${noteWithOctave}`}
                      >
                        {/* Note Badge on Fret */}
                        {(showAllNotes || (gameMode === 'quest' && isTargetFret && feedback?.type === 'success') || isActive || isInspected) ? (
                          <div
                            className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full text-xs font-bold flex items-center justify-center shadow-md transition-all ${
                              isActive
                                ? 'bg-yellow-300 text-yellow-950 scale-125 ring-3 ring-white animate-bounce'
                                : isInspected
                                ? `${stringBadgeStyle} ring-2 ring-white scale-110`
                                : stringBadgeStyle
                            }`}
                          >
                            <span className="leading-none">{note}</span>
                            {showOctaves && (
                              <sub className="text-[9px] font-extrabold opacity-90 -mb-1 ml-0.5">{octave}</sub>
                            )}
                          </div>
                        ) : (
                          <div className="w-5 h-5 rounded-full opacity-0 hover:opacity-100 bg-white/20 hover:scale-110 transition-all flex items-center justify-center text-[10px] text-white">
                            {note}
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              );
            })}
          </div>

          {/* Bottom Position Inlay Markers row */}
          <div className="grid grid-cols-[90px_repeat(13,1fr)] text-center text-amber-400 text-xs pt-1">
            <div />
            {Array.from({ length: 13 }).map((_, f) => (
              <div key={f} className="flex justify-center items-center">
                {f === 12 ? (
                  <div className="flex gap-1">
                    <span className="w-2 h-2 rounded-full bg-amber-200" />
                    <span className="w-2 h-2 rounded-full bg-amber-200" />
                  </div>
                ) : isMarkerFret(f) ? (
                  <span className="w-2 h-2 rounded-full bg-amber-200" />
                ) : null}
              </div>
            ))}
          </div>

        </div>

      </div>

      {/* Helper Legend / Info */}
      <div className="flex flex-wrap items-center justify-between text-xs text-gray-500 bg-white p-4 rounded-2xl border border-pink-100">
        <div className="flex items-center gap-3">
          <span className="font-bold text-purple-900">How to explore:</span>
          <span>Click any fret to hear its exact note tone and see pitch details.</span>
          <span>Each string has its own vibrant color and octave level!</span>
        </div>
        <div className="flex items-center gap-1.5 text-pink-600 font-semibold mt-2 sm:mt-0">
          <Volume2 className="w-4 h-4" />
          <span>Real acoustic string audio generated on click</span>
        </div>
      </div>

    </div>
  );
};
